import CampaignIcon from "@mui/icons-material/Campaign";
import {AppSku} from "crqlar-react-common/dist/models/app/appSku";
import {useCanViewInsights} from "crqlar-react-common/dist/utils/hooks/insights/useCanViewInsights";
import {useMarketplaceApps} from "crqlar-react-common/dist/utils/hooks/app/useMarketplaceApps";
import {useOrganizationApps} from "crqlar-react-common/dist/utils/hooks/app/useOrganizationApps";
import {resolvePermittedApp} from "crqlar-react-common/dist/utils/route/permittedApp";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useLocation, useParams} from "react-router-dom";
import type {TabRoute} from "../layout/PageShell";
import {PageShell} from "../layout/PageShell";
import MarketingInsights from "../insights/MarketingInsights";
import AudiencesList from "./AudiencesList";
import AutomationList from "./automations/AutomationList";
import InsightsList from "./meta/InsightsList";
import NewsletterDashboards from "./newsletter/NewsletterDashboards";
import LifecycleMailsList from "./newsletter/lifecycleMails/LifecycleMailsList";

export default function Marketing() {
    const {t} = useLocalize();
    const {appId: routeAppId} = useParams<"appId">();
    const {pathname} = useLocation();
    const organizationApps = useOrganizationApps();
    const marketplaceApps = useMarketplaceApps();
    const {canViewInsights} = useCanViewInsights();

    const appId = routeAppId ?? resolvePermittedApp({
        organizationApps,
        marketplaceApps,
        pathname,
        matcher: {sku: AppSku.MARKETING},
        legacyName: "Marketing & Campaigns",
    })?.id;

    const appTabs: TabRoute[] = appId
        ? [
            {
                path: `/marketing/audiences/${appId}`,
                label: t("Audiences"),
                render: () => <AudiencesList/>,
            },
            {
                path: `/marketing/newsletter/${appId}`,
                label: t("Newsletter"),
                render: () => <NewsletterDashboards/>,
            },
            {
                path: `/marketing/lifecycle-mails/${appId}`,
                label: t("Lifecycle mails"),
                render: () => <LifecycleMailsList/>,
                hidden: true,
            },
            {
                path: `/marketing/automations/${appId}`,
                label: t("Automations"),
                render: () => <AutomationList/>,
            },
            {
                path: `/marketing/facebook-instagram-ads/${appId}`,
                label: t("Facebook/Instagram Ads"),
                render: () => <InsightsList/>,
            },
        ]
        : [];

    const tabs: TabRoute[] = [
        ...appTabs,
        // Live for the management tier - the same rule the API enforces on the insights endpoints.
        ...(canViewInsights
            ? [
                {
                    path: "/marketing/insights",
                    label: t("Insights"),
                    render: () => <MarketingInsights/>,
                },
            ]
            : []),
    ];

    return (
        <PageShell
            icon={<CampaignIcon fontSize="inherit"/>}
            title={t("Marketing & Campaigns")}
            documentTitle={t("Marketing & Campaigns")}
            tabs={tabs}
        />
    );
}
